<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePeopleStore } from '@/store';
import { can, useReferralAssignCounts } from '@/composables';
import { PERMISSIONS } from '@/enum';

/**
 * Define the people store instance and references.
 */
const peopleStore = usePeopleStore();
const { totals } = storeToRefs(peopleStore);
const { getTotals } = peopleStore;
const referralCounts = useReferralAssignCounts();

/**
 * Define a route instance.
 */
const route = useRoute();
/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Only display the user totals within the user list.
 */
const showTotals = computed<boolean>(
  () =>
    (route.name === 'user.list' || route.name === 'students') &&
    can([PERMISSIONS.PEOPLE_TOTALS, PERMISSIONS.PEOPLE_INDEX_CONNECTED]),
);
const isUserMainPage = computed<boolean>(() => route.name === 'user.list');
const isReferralsPage = computed<boolean>(() => route.name === 'students');

/**
 * Define the total of people available.
 */
const total = computed<number>(() => totals.value.data.total);

/**
 * Define the total or active people.
 */
const active = computed<number>(() => totals.value.data.active);

/**
 * Initialize the people listing and the available types.
 */
const init = async () => {
  if (can([PERMISSIONS.PEOPLE_TOTALS, PERMISSIONS.PEOPLE_INDEX_CONNECTED])) await getTotals();
};
</script>

<template>
  <div v-if="showTotals" class="flex align-items-center justify-content-center text-black h-2rem mt-2 select-none">
    <template v-if="isReferralsPage">
      <div class="left col-fixed p-2 bg-white">
        <span class="mr-1 text-xs font-normal">Total</span>
        <span class="text-sm font-bold" v-text="referralCounts.total.value" />
      </div>
      <div class="right col-fixed p-2 unassigned-badge">
        <span class="mr-1 text-xs font-normal">Unassigned</span>
        <span class="text-sm font-bold" v-text="referralCounts.unassignedTotal.value" />
      </div>
    </template>
    <template v-else>
      <div class="left col-fixed p-2 bg-white">
        <span class="mr-1 text-xs font-normal">Total</span>
        <span class="text-sm font-bold" v-text="total" />
      </div>
      <div v-if="isUserMainPage" class="right col-fixed p-2 bg-white">
        <span class="mr-1 text-xs font-normal">Active</span>
        <span class="text-sm font-bold" v-text="active" />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.flex {
  .left {
    -webkit-border-radius: 6px 0 0 6px;
    -moz-border-radius: 6px 0 0 6px;
    border-radius: 6px 0 0 6px;
  }

  .right {
    -webkit-border-radius: 0 6px 6px 0;
    -moz-border-radius: 0 6px 6px 0;
    border-radius: 0 6px 6px 0;
  }

  .col-fixed {
    margin-left: 1px;
  }

  .unassigned-badge {
    -webkit-border-radius: 6px;
    -moz-border-radius: 6px;
    border-radius: 6px;
    background-color: var(--orange-100, #fff3e0) !important;
    color: var(--orange-700, #e65100);

    span {
      color: var(--orange-700, #e65100);
    }
  }
}
</style>
